import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Box, Truck, Shield, Globe, Download } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CTA from '../components/sections/CTA';
import { Button } from '../components/ui/button';

const ModularPage = () => {
  const [activeModule, setActiveModule] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveModule((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: Box, title: 'Sistema Integrado', description: 'Plataforma completa: diseño, fabricación y delivery en un solo proceso.' },
    { icon: Truck, title: 'Entrega Garantizada', description: 'Logística coordinada desde fábrica hasta sitio de montaje.' },
    { icon: Shield, title: 'Control de Calidad', description: 'Inspección en fábrica bajo estándares europeos.' },
    { icon: Globe, title: 'Replicable Globalmente', description: 'Diseños estandarizados adaptables a cualquier ubicación.' }
  ];

  const projectTypes = [
    { title: 'Vivienda Social', price: 'desde u$s 650/m²', image: 'https://images.unsplash.com/photo-1680975183971-95f4a0d2b508?w=400&auto=format&fit=crop' },
    { title: 'Barrios Cerrados', price: 'desde u$s 700/m²', image: 'https://images.unsplash.com/photo-1712924833046-1c54abac2840?w=400&auto=format&fit=crop' },
    { title: 'Desarrollo Premium', price: 'desde u$s 850/m²', image: 'https://images.unsplash.com/photo-1574848296471-28f79a036f79?w=400&auto=format&fit=crop' },
    { title: 'Hoteles', price: 'desde u$s 990/m²', image: 'https://images.unsplash.com/photo-1629219857214-02b302d42272?w=400&auto=format&fit=crop' }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-neutral-950">
          <div className="absolute inset-0 z-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="absolute w-24 h-16 border border-neutral-800 rounded-sm opacity-20"
                style={{ left: `${(i % 5) * 25}%`, top: `${Math.floor(i / 5) * 25}%`, transform: `rotate(${Math.random() * 10 - 5}deg)` }} />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-950 via-neutral-900/90 to-neutral-950 z-10" />
          <div className="relative z-20 max-w-7xl mx-auto px-6 py-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-blue-400 text-sm font-medium">Plataforma MODE</span>
                </span>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
                  Construcción
                  <span className="block text-blue-500">Modular</span>
                </h1>
                <p className="text-xl text-neutral-400 mb-8 max-w-xl">Casas modulares con diseño británico y manufactura china: eficiencia, calidad y entrega garantizada.</p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" asChild>
                    <Link to="/contact">Solicitar Propuesta <ArrowRight className="ml-2" size={18} /></Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white hover:text-neutral-900 rounded-full">
                    <Download className="mr-2" size={18} />Catálogo Modular
                  </Button>
                </div>
              </div>
              <div className="relative h-96 flex items-center justify-center">
                <div className="relative w-64 h-64">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i}
                      className={`absolute w-28 h-20 rounded-lg transition-all duration-700 ${
                        activeModule === i ? 'bg-blue-500 scale-110 shadow-2xl shadow-blue-500/50' : 'bg-neutral-800'
                      }`}
                      style={{ left: `${(i % 2) * 35}%`, top: `${Math.floor(i / 2) * 45}%`, transform: `perspective(1000px) rotateX(10deg) rotateY(${-10 + i * 5}deg)` }}>
                      <div className="absolute inset-2 border border-white/20 rounded" />
                    </div>
                  ))}
                </div>
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <p className="text-neutral-400 text-xs">Precio desde</p>
                  <p className="text-white text-2xl font-bold">u$s 395<span className="text-sm font-normal">/m²</span></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-wider">Ventajas</span>
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4">Diseño Británico, Manufactura China</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="group p-6 rounded-2xl bg-neutral-50 hover:bg-blue-600 transition-all duration-500">
                    <div className="w-14 h-14 rounded-xl bg-blue-100 group-hover:bg-white/20 flex items-center justify-center mb-4 transition-colors">
                      <Icon className="text-blue-600 group-hover:text-white transition-colors" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-neutral-900 group-hover:text-white mb-2 transition-colors">{feature.title}</h3>
                    <p className="text-neutral-600 group-hover:text-blue-100 transition-colors">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-24 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-blue-600 text-sm font-semibold uppercase tracking-wider">Soluciones</span>
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4">Tipos de Proyecto</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {projectTypes.map((project, index) => (
                <div key={index} className="group relative rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-500">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-neutral-900 mb-1">{project.title}</h3>
                    <p className="text-blue-600 font-semibold">{project.price}</p>
                  </div>
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-500 rounded-2xl transition-colors pointer-events-none" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="text-blue-500 text-sm font-semibold uppercase tracking-wider">Proceso</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4">De Diseño a Entrega</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Diseño', desc: 'Personalización del proyecto' },
                { step: '02', title: 'Fabricación', desc: 'Producción en planta' },
                { step: '03', title: 'Transporte', desc: 'Logística internacional' },
                { step: '04', title: 'Montaje', desc: 'Instalación en sitio' }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="text-6xl font-bold text-blue-500/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-neutral-400">{item.desc}</p>
                  {index < 3 && <div className="hidden md:block absolute top-8 right-0 w-1/2 h-0.5 bg-gradient-to-r from-blue-500/50 to-transparent" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
};

export default ModularPage;
